import { area, curveMonotoneX, line } from 'd3'
import React from 'react'
import { usePlotArea } from 'recharts'

import type { RidgelineRow } from 'features/charts-v2/services/data-transformation-service'
import { CustomYAxisTick } from 'features/charts-v2/utils/AxisTickUtils'
import { formatMetricValue } from 'features/charts-v2/utils/metric-format-utils'
import {
  getRidgelineColorBands,
  type RidgelineMetric,
} from 'features/charts-v2/utils/ridgeline-bins'

// A ridge may rise this many row-bands above its own baseline, so the tallest
// ridge overlaps the row above it. The plot reserves the same headroom on top.
const RIDGE_OVERLAP_BANDS = 0.6
const RIDGE_FILL_OPACITY = 0.55
const MEAN_TICK_BAND_FRACTION = 0.5
const MEAN_LABEL_GAP_PX = 6
const Y_LABEL_GAP_PX = 8
const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'

export interface RidgelineLayerRow extends RidgelineRow {
  /** Colour of the row mean (the tooltip swatch); the ridge itself is coloured
   *  by x-axis band, not by this. */
  color: string
}

/** How the row names (left) and mean values (right) are drawn. */
export interface RidgelineLabelLayout {
  showRowNames: boolean
  showMeans: boolean
  fontSizePx: number
  overflow: 'expand' | 'wrap' | 'truncate'
  truncateChars: number
  wrapWidthPx: number
  /** Show every Nth row name; the rest fade out. */
  rowNameStep: number
}

export interface RidgelineHandlers {
  onHover: (
    rowIndex: number,
    binIndex: number,
    clientX: number,
    clientY: number,
  ) => void
  onLeave: () => void
  onClick: (rowIndex: number, binIndex: number, event: React.MouseEvent) => void
}

interface RidgelineLayerProps {
  rows: RidgelineLayerRow[]
  metric: RidgelineMetric
  xDomain: [number, number]
  /** True: heights are raw counts. False: heights are % of the row. */
  scaleByVolume: boolean
  labels: RidgelineLabelLayout
  handlers: RidgelineHandlers
}

interface Point {
  x: number
  y: number
}

const smoothLine = line<Point>()
  .x((point) => point.x)
  .y((point) => point.y)
  .curve(curveMonotoneX)

const binValue = (
  bin: RidgelineRow['bins'][number],
  scaleByVolume: boolean,
): number => (scaleByVolume ? bin.count : bin.pct)

const RidgelineLayerComponent: React.FC<RidgelineLayerProps> = ({
  rows,
  metric,
  xDomain,
  scaleByVolume,
  labels,
  handlers,
}) => {
  const plot = usePlotArea()
  // Unique per chart instance, so two ridgeline charts on one dashboard can't
  // share clip ids. useId's colons are not valid in a url(#…) reference.
  const clipIdPrefix = `ridgeline-band-${React.useId().replace(/:/g, '')}`
  if (!plot || rows.length === 0) return null

  const [domainMin, domainMax] = xDomain
  const colorBands = getRidgelineColorBands(metric, xDomain)
  const clipIdOf = (bandIndex: number): string => `${clipIdPrefix}-${bandIndex}`
  const xOf = (value: number): number =>
    plot.x + ((value - domainMin) / (domainMax - domainMin)) * plot.width

  // One shared scale across every ridge, so rows stay comparable.
  const maxValue = Math.max(
    0,
    ...rows.flatMap((row) =>
      row.bins.map((bin) => binValue(bin, scaleByVolume)),
    ),
  )
  const band = plot.height / (rows.length + RIDGE_OVERLAP_BANDS)
  const maxRidgeHeight = (1 + RIDGE_OVERLAP_BANDS) * band
  const baselineOf = (rowIndex: number): number =>
    plot.y + (RIDGE_OVERLAP_BANDS + rowIndex + 1) * band

  const nearestBinIndex = (
    rowIndex: number,
    event: React.MouseEvent<SVGElement>,
  ): number => {
    const svgLeft =
      event.currentTarget.ownerSVGElement?.getBoundingClientRect().left ?? 0
    const localX = event.clientX - svgLeft
    const bins = rows[rowIndex]!.bins
    let nearest = 0
    bins.forEach((bin, binIndex) => {
      if (
        Math.abs(xOf(bin.x) - localX) < Math.abs(xOf(bins[nearest]!.x) - localX)
      ) {
        nearest = binIndex
      }
    })
    return nearest
  }

  const handlePointer =
    (rowIndex: number) =>
    (event: React.MouseEvent<SVGElement>): void => {
      handlers.onHover(
        rowIndex,
        nearestBinIndex(rowIndex, event),
        event.clientX,
        event.clientY,
      )
    }

  const handleClick =
    (rowIndex: number) =>
    (event: React.MouseEvent<SVGElement>): void => {
      handlers.onClick(rowIndex, nearestBinIndex(rowIndex, event), event)
    }

  const ridgePoints = (row: RidgelineLayerRow, baseline: number): Point[] => {
    const points = row.bins.map((bin) => ({
      x: xOf(bin.x),
      y:
        baseline -
        (maxValue > 0
          ? (binValue(bin, scaleByVolume) / maxValue) * maxRidgeHeight
          : 0),
    }))
    // Sentiment bins stop short of the axis ends; close the curve to zero there.
    return metric === 'sentiment'
      ? [
          { x: xOf(domainMin), y: baseline },
          ...points,
          { x: xOf(domainMax), y: baseline },
        ]
      : points
  }

  return (
    <g className="ridgeline-layer">
      {/* Every ridge is drawn once per colour band, each copy clipped to its
          band's x-range. All rows share one x scale, so the clips are shared. */}
      <defs>
        {colorBands.map((colorBand, bandIndex) => (
          <clipPath key={clipIdOf(bandIndex)} id={clipIdOf(bandIndex)}>
            <rect
              x={xOf(colorBand.from)}
              y={0}
              width={xOf(colorBand.to) - xOf(colorBand.from)}
              height={plot.y + plot.height}
            />
          </clipPath>
        ))}
      </defs>

      {rows.map((_, rowIndex) => (
        <rect
          key={`hit-${rowIndex}`}
          x={plot.x}
          y={baselineOf(rowIndex) - band}
          width={plot.width}
          height={band}
          fill="transparent"
          onMouseMove={handlePointer(rowIndex)}
          onMouseLeave={handlers.onLeave}
          onClick={handleClick(rowIndex)}
        />
      ))}

      {rows.map((row, rowIndex) => {
        const baseline = baselineOf(rowIndex)
        const points = ridgePoints(row, baseline)
        const fillPath = area<Point>()
          .x((point) => point.x)
          .y0(baseline)
          .y1((point) => point.y)
          .curve(curveMonotoneX)(points)
        const outlinePath = smoothLine(points)
        return (
          <g key={`ridge-${rowIndex}`}>
            <line
              x1={plot.x}
              x2={plot.x + plot.width}
              y1={baseline}
              y2={baseline}
              stroke="#e5e5e5"
              pointerEvents="none"
            />
            {colorBands.map((colorBand, bandIndex) => (
              <path
                key={`fill-${bandIndex}`}
                d={fillPath ?? undefined}
                clipPath={`url(#${clipIdOf(bandIndex)})`}
                fill={colorBand.color}
                fillOpacity={RIDGE_FILL_OPACITY}
                stroke="none"
                cursor="pointer"
                data-row-name={row.name}
                data-metric-bucket={colorBand.bucket}
                onMouseMove={handlePointer(rowIndex)}
                onMouseLeave={handlers.onLeave}
                onClick={handleClick(rowIndex)}
              />
            ))}
            {colorBands.map((colorBand, bandIndex) => (
              <path
                key={`outline-${bandIndex}`}
                d={outlinePath ?? undefined}
                clipPath={`url(#${clipIdOf(bandIndex)})`}
                fill="none"
                stroke={colorBand.color}
                strokeWidth={1.5}
                pointerEvents="none"
                data-row-name={row.name}
                data-metric-bucket={colorBand.bucket}
              />
            ))}
            {row.mean != null && (
              <line
                x1={xOf(row.mean)}
                x2={xOf(row.mean)}
                y1={baseline}
                y2={baseline - band * MEAN_TICK_BAND_FRACTION}
                stroke="#333"
                strokeWidth={1.5}
                pointerEvents="none"
              />
            )}
            {labels.showMeans && row.mean != null && (
              <text
                x={plot.x + plot.width + MEAN_LABEL_GAP_PX}
                y={baseline}
                fill="#333"
                fontSize={labels.fontSizePx}
                fontFamily={FONT_FAMILY}
                textAnchor="start"
                pointerEvents="none"
              >
                {formatMetricValue(row.mean, metric)}
              </text>
            )}
            {labels.showRowNames && (
              <CustomYAxisTick
                x={plot.x - Y_LABEL_GAP_PX}
                y={baseline - band * 0.25}
                payload={{ value: row.name }}
                fontSizePx={labels.fontSizePx}
                overflowMode={labels.overflow}
                truncateAtChars={labels.truncateChars}
                wrapAtPx={labels.wrapWidthPx}
                index={rowIndex}
                step={labels.rowNameStep}
              />
            )}
          </g>
        )
      })}
    </g>
  )
}

export const RidgelineLayer = React.memo(RidgelineLayerComponent)
